import {
  Actions,
  ofType,
  Effect
} from '@ngrx/effects';
import {
  DocumentActionTypes,
  LoadDocuments,
  DocumentAdded,
  CreateDocument,
  DeleteDocument,
  UpdateDocument,
  GetDocument,
  CreateOrUpdateDocument,
  DocumentCreated,
  DocumentUpdated,
  DocumentDeleted,
  RequestDeleteDocument,
  SubscribeDocuments,
  GetManyDocument,
  AddManyDocuments,
  RestoreDocument,
  ArchiveDocument,
  ImportDocuments,
  CreateManyDocuments,
  ExportDocuments,
  DocumentRemoved,
  DocumentModified,
  SetDocumentAsDraft,
  SetDocumentAsTemplate,
  ClearAll,
  OpenDeleteDialog,
  CreateFailed,
  DeleteFailed,
  UpdateFailed
} from './document.actions';
import {
  mergeMap,
  map,
  filter,
  switchMap,
  catchError,
  bufferTime,
  delay,
  tap,
  last,
  first,
  timeout
} from 'rxjs/operators';
import { Injectable } from '@angular/core';
import {
  of,
  fromEvent,
  Observable,
  from,
  defer
} from 'rxjs';
import { NGXLogger } from 'ngx-logger';
import {
  Store,
  select
} from '@ngrx/store';
import { DigiAIXCoreAppState } from '../app-state';
import { ShowErrorMessage } from '../notification/notification.actions';
import { DocumentService } from './document.service';
import { chunk } from './rxjs-operators';
import {
  selectDocumentState,
  getDocumentById
} from './document.selectors';
import { Router } from '@angular/router';
import { LifeCycleService } from '../life-cycle/life-cycle.service';
import { FireStoreDocument } from '@digitaix/types';

@Injectable()
export class DocumentEffects<
  DocumentType extends FireStoreDocument,
  AppState extends DigiAIXCoreAppState,
  Query
> {
  @Effect({ resubscribeOnError: true })
  public loadDocuments$: Observable<AddManyDocuments<any> | ShowErrorMessage > = this.actions$.pipe(
    ofType<LoadDocuments<Query>>(DocumentActionTypes.Load),
    mergeMap((action) =>
      this.ds
        .load(action.collectionPath, action.query)
        .pipe(
          map(
            (documents) =>
              new AddManyDocuments(
                action.collectionPath,
                documents,
                action.tracer || '[DocumentEffects::loadDocuments$]',
                (action.stack || []).concat([
                  '[DocumentEffects::loadDocuments$]',
                ]),
              ),
          ),
          catchError((err) => {
            this.logger.error('[DocumentEffects::loadDocuments$]', err);
            return of(new ShowErrorMessage(err.message, err));
          }),
        ),
    ),
  );

  @Effect({ resubscribeOnError: true })
  public subscribeDocuments$: Observable<DocumentAdded<any> | DocumentRemoved | DocumentModified<any> | ShowErrorMessage> = this.actions$.pipe(
    ofType<SubscribeDocuments<Query>>(DocumentActionTypes.Subscribe),
    mergeMap((action) =>
      this.ds.subscribe(action.collectionPath, action.query),
    ),
  );

  @Effect({ resubscribeOnError: true })
  public setDocumentAsDraftToggle$: Observable<UpdateDocument<any>> = this.actions$.pipe(
    ofType<SetDocumentAsDraft>(DocumentActionTypes.Draft),
    filter((action) => action.draft === undefined),
    switchMap((action) =>
      this.store$
        .select(getDocumentById(action.collectionPath, action.documentId))
        .pipe(
          map(
            (document) =>
              new UpdateDocument(action.collectionPath, action.documentId, {
                __draft: !document.__draft,
              }),
          ),
        ),
    ),
  );

  @Effect({ resubscribeOnError: true })
  public setDocumentAsDraft$: Observable<UpdateDocument<any>> = this.actions$.pipe(
    ofType<SetDocumentAsDraft>(DocumentActionTypes.Draft),
    filter((action) => action.draft !== undefined),
    map(
      (action) =>
        new UpdateDocument(action.collectionPath, action.documentId, {
          __draft: action.draft,
        }),
    ),
  );

  @Effect({ resubscribeOnError: true })
  public setDocumentAsTemplateToggle$: Observable<UpdateDocument<any>> = this.actions$.pipe(
    ofType<SetDocumentAsTemplate>(DocumentActionTypes.Template),
    filter((action) => action.template === undefined),
    switchMap((action) =>
      this.store$
        .select(getDocumentById(action.collectionPath, action.documentId))
        .pipe(
          map(
            (document) =>
              new UpdateDocument(action.collectionPath, action.documentId, {
                __template: !document.__template,
              }),
          ),
        ),
    ),
  );

  @Effect({ resubscribeOnError: true })
  public setDocumentAsTemplate$: Observable<UpdateDocument<any>> = this.actions$.pipe(
    ofType<SetDocumentAsTemplate>(DocumentActionTypes.Template),
    filter((action) => action.template !== undefined),
    map(
      (action) =>
        new UpdateDocument(action.collectionPath, action.documentId, {
          __template: action.template,
        }),
    ),
  );

  @Effect({ resubscribeOnError: true })
  public createDocument$: Observable<ShowErrorMessage | CreateFailed<any> | DocumentCreated<any>> = this.lifeCycle.whenStable(() => defer(() => this.actions$.pipe(
    ofType<CreateDocument<DocumentType>>(DocumentActionTypes.Create),
    map((action) => {
      if (action.document.hasOwnProperty('id')) {
        const doc = { ...action.document };
        delete doc[ 'id' ];
        return new CreateDocument(action.collectionPath, doc, action.id, action.tracer, action.stack);
      }
      return action;
    }),
    bufferTime(100),
    delay(100),
    mergeMap((a) => a),
    mergeMap((action) =>
      this.ds.create(action.collectionPath, action.document, action.id).pipe(
        map(
          (document: FireStoreDocument) =>
            new DocumentCreated(
              document || null,
              action.collectionPath,
              action.tracer || '[DocumentEffects::createDocument$]',
              (action.stack || []).concat([
                '[DocumentEffects::createDocument$]'
              ])
            )
        ),
        catchError((err) => {
          this.logger.error('[DocumentEffects::createDocument$]', err);
          return of(
            new ShowErrorMessage(err.message, err),
            new CreateFailed(
              action.collectionPath,
              action.document,
              err,
              action.tracer,
              (action.stack || []).concat([
                '[DocumentEffects::createDocument$]'
              ])
            )
          );
        })
      )
    )
  )));

  @Effect({ resubscribeOnError: true })
  public createOrUpdateDocument$: Observable<UpdateDocument<any> | CreateDocument<any>> = this.actions$.pipe(
    ofType<CreateOrUpdateDocument<DocumentType>>(
      DocumentActionTypes.CreateOrUpdate,
    ),
    map((action) => {
      if (action.document.id) {
        return new UpdateDocument<DocumentType>(
          action.collectionPath,
          action.document.id,
          action.document,
          action.tracer || '[DocumentEffects::createOrUpdateDocument$]',
          (action.stack || []).concat([
            '[DocumentEffects::createOrUpdateDocument$]',
          ]),
        );
      } else {
        return new CreateDocument<DocumentType>(
          action.collectionPath,
          action.document,
          undefined,
          action.tracer || '[DocumentEffects::createOrUpdateDocument$]',
          (action.stack || []).concat([
            '[DocumentEffects::createOrUpdateDocument$]',
          ]),
        );
      }
    }),
  );

  @Effect({ dispatch: false, resubscribeOnError: true })
  public clearAll$: Observable<any> = this.actions$.pipe(
    ofType<ClearAll>(DocumentActionTypes.ClearAll),
    tap(() => this.ds.reset(false)),
    filter((action) => action.navigate),
    delay(500),
    tap(() => this.router.navigate(['/'])),
  );

  @Effect({ resubscribeOnError: true })
  public requestDeleteDocument$: Observable<OpenDeleteDialog> = this.actions$.pipe(
    ofType<RequestDeleteDocument>(DocumentActionTypes.RequestDelete),
    map(action => new OpenDeleteDialog(
      action.message,
      action.collectionPath,
      action.id,
      action.tracer || '[DocumentEffects::requestDeleteDocument$]',
      (action.stack || []).concat([
        '[DocumentEffects::requestDeleteDocument$]',
      ]),
    )),

  );

  @Effect({ resubscribeOnError: true })
  public deleteDocument$: Observable<ShowErrorMessage | DeleteFailed<any> | DocumentDeleted> = this.actions$.pipe(
    ofType<DeleteDocument>(DocumentActionTypes.Delete),
    mergeMap((action) =>
      this.ds.delete(action.collectionPath, action.id).pipe(
        map(
          () =>
            new DocumentDeleted(
              action.id,
              action.collectionPath,
              action.tracer || '[DocumentEffects::deleteDocument$]',
              (action.stack || []).concat([
                '[DocumentEffects::deleteDocument$]',
              ]),
            ),
        ),
        catchError((err) => {
          this.logger.error('[DocumentEffects::deleteDocument$]', err);
          return of(
            new ShowErrorMessage(err.message, err),
            new DeleteFailed(
              action.collectionPath,
              action.id,
              err,
              action.tracer,
              (action.stack || []).concat([
                '[DocumentEffects::createDocument$]',
              ]),
            )
          );
        }),
      ),
    ),
  );

  @Effect({ resubscribeOnError: true })
  public updateDocument$: Observable<UpdateFailed<any> | ShowErrorMessage | DocumentUpdated<any> > = this.actions$.pipe(
    ofType<UpdateDocument<DocumentType>>(DocumentActionTypes.Update),
    map((action) => {
      if (action.document.hasOwnProperty('id')) {
        const doc = { ...action.document };
        delete doc['id'];
        return new UpdateDocument(action.collectionPath, action.id, doc, action.tracer, action.stack)
      }
      return action;
    }),
    mergeMap((action) =>
      this.ds.update(action.collectionPath, action.id, action.document).pipe(
        map(
          (document) =>
            new DocumentUpdated(
              document,
              action.collectionPath,
              action.tracer || '[DocumentEffects::updateDocument$]',
              (action.stack || []).concat([
                '[DocumentEffects::updateDocument$]',
              ]),
            ),
        ),
        catchError((err) => {
          this.logger.error('[DocumentEffects::updateDocument$]', err);
          return of(
            new ShowErrorMessage(err.message, err),
            new UpdateFailed(
              action.collectionPath,
              action.id,
              action.document,
              err,
              action.tracer,
              (action.stack || []).concat([
                '[DocumentEffects::createDocument$]',
              ]),
            )
          );
        }),
      ),
    ),
  );

  @Effect({ resubscribeOnError: true })
  public documentCreated$: Observable<DocumentAdded<any>> = this.lifeCycle.whenStable(() => this.actions$.pipe(
    ofType<DocumentCreated<FireStoreDocument>>(DocumentActionTypes.Created),
    chunk(),
    map(
      (action) =>
        new DocumentAdded(
          action.collectionPath,
          action.document,
          action.tracer || '[DocumentEffects::documentCreated$]',
          (action.stack || []).concat(['[DocumentEffects::documentCreated$]']),
        ),
    ),
  ));

  @Effect({ resubscribeOnError: true })
  public documentDeleted$: Observable<DocumentRemoved> = this.actions$.pipe(
    ofType<DocumentDeleted>(DocumentActionTypes.Deleted),
    map(
      (action) =>
        new DocumentRemoved(
          action.collectionPath,
          action.id,
          action.tracer || '[DocumentEffects::documentDeleted$]',
          (action.stack || []).concat(['[DocumentEffects::documentDeleted$]']),
        ),
    ),
  );

  @Effect({ resubscribeOnError: true })
  public documentUpdated$: Observable<DocumentModified<any>> = this.actions$.pipe(
    ofType<DocumentUpdated<FireStoreDocument>>(DocumentActionTypes.Updated),
    map(
      (action) =>
        new DocumentModified(
          action.collectionPath,
          action.document,
          action.tracer || '[DocumentEffects::documentUpdated$]',
          (action.stack || []).concat(['[DocumentEffects::documentUpdated$]']),
        ),
    ),
  );

  @Effect({ resubscribeOnError: true })
  public getDocument$: Observable<ShowErrorMessage | DocumentAdded<any>> = this.actions$.pipe(
    ofType<GetDocument>(DocumentActionTypes.Get),
    mergeMap((action) =>
      this.ds.get(action.collectionPath, action.id).pipe(
        filter((document) => document !== null),
        map(
          (document: FireStoreDocument) =>
            new DocumentAdded(
              action.collectionPath,
              document,
              action.tracer || '[DocumentEffects::getDocument$]',
              (action.stack || []).concat(['[DocumentEffects::getDocument$]']),
            ),
        ),
        catchError((err) => {
          this.logger.error('[DocumentEffects::getDocument$]', err);
          return of(new ShowErrorMessage(err.message, err));
        }),
      ),
    ),
  );

  @Effect({ resubscribeOnError: true })
  public getManyDocument$: Observable<AddManyDocuments<any> | ShowErrorMessage> = this.actions$.pipe(
    ofType<GetManyDocument>(DocumentActionTypes.GetMany),
    switchMap((action) =>
      this.ds.getMany(action.collectionPath, action.ids).pipe(
        filter((document) => document !== null),
        map(
          (documents: FireStoreDocument[]) =>
            new AddManyDocuments(
              action.collectionPath,
              documents,
              action.tracer || '[DocumentEffects::getManyDocument$]',
              (action.stack || []).concat([
                '[DocumentEffects::getManyDocument$]',
              ]),
            ),
        ),
        catchError((err) => {
          this.logger.error('[DocumentEffects::getManyDocument$]', err);
          return of(new ShowErrorMessage(err.message, err));
        }),
      ),
    ),
  );

  @Effect({ resubscribeOnError: true })
  public restoreDocument$: Observable<UpdateDocument<any>> = this.actions$.pipe(
    ofType<RestoreDocument>(DocumentActionTypes.Restore),
    map(
      (action) =>
        new UpdateDocument(
          action.collectionPath,
          action.documentId,
          {
            __archived: false,
          },
          action.tracer || '[DocumentEffects::restoreDocument$]',
          (action.stack || []).concat(['[DocumentEffects::restoreDocument$]']),
        ),
    ),
  );

  @Effect({ resubscribeOnError: true })
  public archiveDocument$: Observable<UpdateDocument<any>> = this.actions$.pipe(
    ofType<ArchiveDocument>(DocumentActionTypes.Archive),
    map(
      (action) =>
        new UpdateDocument(
          action.collectionPath,
          action.documentId,
          {
            __archived: true,
          },
          action.tracer || '[DocumentEffects::archiveDocument$]',
          (action.stack || []).concat(['[DocumentEffects::archiveDocument$]']),
        ),
    ),
  );

  @Effect({ resubscribeOnError: true })
  public createMany$: Observable<CreateOrUpdateDocument<any>> = this.actions$.pipe(
    ofType<CreateManyDocuments<FireStoreDocument>>(
      DocumentActionTypes.CreateMany,
    ),
    mergeMap((action) =>
      from(
        action.documents.map(
          (document) =>
            new CreateOrUpdateDocument(
              action.collectionPath,
              document,
              action.tracer || '[DocumentEffects::createMany$]',
              (action.stack || []).concat(['[DocumentEffects::createMany$]']),
            ),
        ),
      ).pipe(chunk()),
    ),
  );

  @Effect({ resubscribeOnError: true })
  public importDocuments$: Observable<CreateManyDocuments<any> | ShowErrorMessage> = this.actions$.pipe(
    ofType<ImportDocuments>(DocumentActionTypes.Import),
    switchMap(() => {
      const input = document.createElement('input');
      input.setAttribute('type', 'file');
      input.setAttribute('accept', 'application/json');

      const change$ = fromEvent(input, 'change');

      input.click();

      return change$;
    }),
    switchMap((event: any) => {
      const files: FileList = event.target.files;

      if (files && files.length) {
        const file = files.item(0);
        const reader = new FileReader();

        const load$ = new Observable((subscriber) => {
          reader.onload = (pe: any) => {
            subscriber.next(pe);
            if (pe.target.readyState === 2) {
              subscriber.complete();
            } else if (pe.target.error) {
              subscriber.error(pe.target.error);
            }
          };
        }).pipe(
          last(),
          map((pe: any) => pe.target.result),
          tap((result) =>
            this.logger.debug(
              '[DocumentEffects::importDocuments$] file loaded',
              result,
            ),
          ),
          map((result) => {
            try {
              return JSON.parse(result);
            } catch (e) {
              this.logger.error(
                '[DocumentEffects::importDocuments$] parse loaded file to json failed',
                result,
              );
              return null;
            }
          }),
          filter(Boolean),
          switchMap((importDocuments) =>
            Object.entries(importDocuments).map(
              ([collectionPath, documents]: [string, FireStoreDocument[]]) =>
                new CreateManyDocuments(
                  collectionPath,
                  documents,
                  '[DocumentEffects::importDocuments$]',
                  ['[DocumentEffects::importDocuments$]'],
                ),
            ),
          ),
          chunk(1, 10),
        );

        reader.readAsText(file);

        return load$;
      } else {
        this.logger.error(
          '[DocumentEffects::importDocuments$] file list is empty',
          event,
        );
        return of(new ShowErrorMessage('Document import failed'));
      }
    }),
  );

  @Effect({ dispatch: false, resubscribeOnError: true })
  public exportDocuments$: Observable<any> = this.actions$.pipe(
    ofType<ExportDocuments>(DocumentActionTypes.Export),
    switchMap((action) =>
      this.store$.pipe(
        select(selectDocumentState),
        first(),
        map((documentState) => {
          let collectionPaths: string[] = Object.keys(documentState);

          if (action.payload) {
            if (action.payload.exclude) {
              collectionPaths = collectionPaths.filter(
                (cp) => !action.payload.exclude.includes(cp),
              );
            }
            if (action.payload.include) {
              collectionPaths = collectionPaths.filter((cp) =>
                action.payload.include.includes(cp),
              );
            }
          }

          return collectionPaths.reduce(
            (acc, cp) => ({ ...acc, [cp]: documentState[cp].entities }),
            {},
          );
        }),
        map((documentMap) => JSON.stringify(documentMap)),
        tap((content) => {
          const element = document.createElement('a');
          element.setAttribute(
            'href',
            'data:application/json;charset=utf-8,' +
              encodeURIComponent(content),
          );
          element.setAttribute(
            'download',
            `${new Date().toISOString()}_export.json`,
          );

          element.style.display = 'none';
          document.body.appendChild(element);

          element.click();

          document.body.removeChild(element);
        }),
      ),
    ),
  );

  constructor(
    public readonly actions$: Actions,
    public readonly logger: NGXLogger,
    public readonly store$: Store<AppState>,
    public readonly lifeCycle: LifeCycleService,
    public readonly ds: DocumentService<DocumentType, any>,
    public readonly router: Router,
  ) {}
}
